import React, { useEffect, useState, useRef, useCallback } from "react";
import FilterBar from "../components/FilterBar";
import SearchPageTopBar from "../components/SearchPageTopBar";
import HotelCard from "../components/HotelCard";
import DefaultSEO from "../components/DefaultSEO";
import { useLocation, useNavigate } from "react-router-dom";
import { propertyService } from "../services/propertyService";

// Helper function to extract base pricing from dynamic rooms.
const extractPrice = (rooms, type) => {
    if (!rooms || rooms.length === 0) return 0;
    const lowest = Math.min(...rooms.map(r => Number(r.base_price_per_night || 0)));
    return type === 'price' ? lowest : lowest + (lowest * 0.2);
};

const extractAmenities = (amenities) => {
    if (!amenities || !Array.isArray(amenities)) return [];
    return amenities.map(a => ({ icon: "check", label: typeof a === 'string' ? a : a.label }));
};

// Reverse geocode latitude/longitude using Nominatim API
const getCityFromLatLng = async (latitude, longitude) => {
    try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
        const data = await res.json();
        const cityName = data?.address?.city || data?.address?.town || data?.address?.village || "";
        if (cityName) {
            return cityName;
        }
        throw new Error("City name not found in Nominatim response");
    } catch (error) {
        console.error("Nominatim reverse geocoding failed:", error);
        throw error;
    }
};

// Skeleton Loader Component
const HotelSkeletonCard = () => (
    <div className="bg-white overflow-hidden border-b border-gray-200 rounded-none mx-0 mb-0 lg:bg-white lg:rounded-2xl lg:mb-6 lg:shadow-sm lg:border lg:border-gray-100 animate-pulse">
        {/* Mobile Layout */}
        <div className="block lg:hidden p-3">
            <div className="flex gap-3">
                <div className="w-[110px] h-[130px] bg-gray-200 rounded-xl flex-shrink-0"></div>
                <div className="flex-1 flex flex-col justify-between py-0.5">
                    <div>
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2 mb-2.5"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3 mb-2"></div>
                        <div className="flex gap-1 mb-1">
                            <div className="h-4 bg-gray-200 rounded w-12"></div>
                            <div className="h-4 bg-gray-200 rounded w-12"></div>
                        </div>
                    </div>
                    <div className="flex items-end justify-between mt-auto">
                        <div className="w-1/2">
                            <div className="h-5 bg-gray-200 rounded w-16 mb-1"></div>
                            <div className="h-2 bg-gray-200 rounded w-20"></div>
                        </div>
                        <div className="h-7 bg-gray-200 rounded-lg w-14"></div>
                    </div>
                </div>
            </div>
        </div>
        {/* Desktop Layout */}
        <div className="hidden lg:flex gap-6 p-5">
            <div className="w-[320px] flex-shrink-0">
                <div className="w-full h-52 bg-gray-200 rounded-2xl"></div>
                <div className="flex gap-2 mt-3">
                    <div className="w-16 h-12 bg-gray-200 rounded-lg"></div>
                    <div className="w-16 h-12 bg-gray-200 rounded-lg"></div>
                    <div className="w-16 h-12 bg-gray-200 rounded-lg"></div>
                </div>
            </div>
            <div className="flex-1 flex flex-col pt-2">
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="flex gap-2 mb-4">
                    <div className="h-8 bg-gray-200 rounded-md w-16"></div>
                    <div className="h-6 bg-gray-200 rounded-md w-32 mt-1"></div>
                </div>
                <div className="flex gap-2 mb-6">
                    <div className="h-6 bg-gray-200 rounded-md w-20"></div>
                    <div className="h-6 bg-gray-200 rounded-md w-24"></div>
                </div>
                <div className="flex justify-between items-end mt-auto pt-4 border-t border-dashed border-gray-200">
                    <div>
                        <div className="h-8 bg-gray-200 rounded w-24 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-32"></div>
                    </div>
                    <div className="flex gap-2">
                        <div className="h-10 bg-gray-200 rounded-lg w-28"></div>
                        <div className="h-10 bg-gray-200 rounded-lg w-28"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// Map hotel object to HotelCard props
const mapHotelToCard = (hotel, navigate, location) => {
    const price = hotel.min_price ? Number(hotel.min_price) : extractPrice(hotel.rooms, 'price');
    const oPrice = hotel.min_price ? Math.round(Number(hotel.min_price) * 1.2) : extractPrice(hotel.rooms, 'original');
    const isSoldOut = hotel.provider === 'ratehawk' ? (hotel.is_available === false) : (hotel.is_sold_out || false);

    return {
        id: hotel.id,
        images: hotel.images ? hotel.images.map(img => {
            if (typeof img === 'string') return img.startsWith('http') ? img : `${process.env.REACT_APP_STORAGE_URL || (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '') + '/storage'}/${img}`;
            const p = img?.file_path || img?.url || '';
            return p.startsWith('http') ? p : `${process.env.REACT_APP_STORAGE_URL || (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '') + '/storage'}/${p}`;
        }) : [],
        title: hotel.name,
        location: hotel.address ? `${hotel.address}, ${hotel.city}` : `${hotel.city}, ${hotel.state || ''}`,
        distance: hotel.distance_from_airport ? `${hotel.distance_from_airport} from airport` : "",
        rating: Number(hotel.star_rating) || 4.0,
        ratingCount: Math.floor(Math.random() * 300) + 50,
        ratingText: Number(hotel.star_rating) >= 4 ? "Excellent" : "Very Good",
        amenities: extractAmenities(hotel.amenities),
        price: price > 0 ? price : 1500,
        originalPrice: oPrice > 0 ? oPrice : 2000,
        isSoldOut: isSoldOut,
        discountPercent: Math.round(((oPrice - price) / oPrice) * 100) || 12,
        taxesNote: "+ taxes & fees · per room per night",
        onViewDetails: () => navigate(`/hotel/${hotel.id}${location.search}`, { state: { isSoldOut } }),
        onBookNow: () => navigate(`/hotel/${hotel.id}${location.search}`, { state: { isSoldOut } }),
        onFavorite: () => console.log("Added to favorites"),
    };
};

// Main Search Page Component
const SearchPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [showFilter, setShowFilter] = useState(false);
    const [showSortSheet, setShowSortSheet] = useState(false);
    const [sortBy, setSortBy] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("sort") || "popular";
    });
    const [city, setCity] = useState("");
    const [isSearching, setIsSearching] = useState(true);
    const [locationLoaded, setLocationLoaded] = useState(false);
    const [hotels, setHotels] = useState([]);

    // Scrollable / Infinite Loading State
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const observerTarget = useRef(null);
    const isLoadingMoreRef = useRef(false);
    const hasMoreRef = useRef(true);
    const pageRef = useRef(1);
    const isSearchingRef = useRef(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const cityParam = params.get("city");
        if (cityParam) {
            let decoded = cityParam;
            try {
                decoded = decodeURIComponent(cityParam);
                if (decoded.includes('%')) {
                    decoded = decodeURIComponent(decoded);
                }
            } catch (e) {
                console.error("Failed to decode city parameter:", e);
            }
            setCity(decoded);
            setLocationLoaded(true);
        } else {
            // Fetch user's current location by default and view hotels
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const { latitude, longitude } = position.coords;
                        try {
                            const cityName = await getCityFromLatLng(latitude, longitude);
                            if (cityName) {
                                setCity(cityName);
                            }
                        } catch (error) {
                            console.error("Reverse geocoding failed:", error);
                        }
                        setLocationLoaded(true);
                    },
                    (error) => {
                        console.error("Error getting geolocation:", error);
                        setLocationLoaded(true);
                    }
                );
            } else {
                setLocationLoaded(true);
            }
        }
    }, [location.search]);

    const [filters, setFilters] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return {
            price: params.get("price") ? parseInt(params.get("price"), 10) : 5000,
            amenities: params.get("amenities") ? params.get("amenities").split(',') : [],
            rating: params.get("rating") ? parseInt(params.get("rating"), 10) : null,
            accommodationType: params.get("property_type") ? params.get("property_type").split(',') : [],
            coupleFriendly: params.get("couple_friendly") === '1',
            petsAllowed: params.get("pets_allowed") === '1',
        };
    });

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        let changed = false;

        const updateParam = (key, value) => {
            if (value !== undefined && value !== null && value !== '' && value !== false && value !== 5000 && value !== 'popular') {
                if (params.get(key) !== String(value)) {
                    params.set(key, value);
                    changed = true;
                }
            } else {
                if (params.has(key)) {
                    params.delete(key);
                    changed = true;
                }
            }
        };

        updateParam("price", filters.price);
        updateParam("amenities", filters.amenities.length > 0 ? filters.amenities.join(',') : null);
        updateParam("rating", filters.rating);
        updateParam("property_type", filters.accommodationType.length > 0 ? filters.accommodationType.join(',') : null);
        updateParam("couple_friendly", filters.coupleFriendly ? '1' : null);
        updateParam("pets_allowed", filters.petsAllowed ? '1' : null);
        updateParam("sort", sortBy);

        if (changed) {
            navigate(`?${params.toString()}`, { replace: true });
        }
    }, [filters, sortBy, navigate]);

    // Build query params helper for initial and pagination calls
    const buildQueryParams = useCallback((pageNum = 1) => {
        const params = new URLSearchParams(location.search);
        const checkInDate = params.get("checkin");
        const checkOutDate = params.get("checkout");
        const guestsParam = params.get("guests");

        const queryParams = {
            location: city || undefined,
            check_in: checkInDate || undefined,
            check_out: checkOutDate || undefined,
            adults: guestsParam || undefined,
            max_price: filters.price < 5000 ? filters.price : undefined,
            amenities: filters.amenities.length > 0 ? filters.amenities.join(',') : undefined,
            star_rating: filters.rating || undefined,
            property_type: filters.accommodationType.length > 0 ? filters.accommodationType.join(',') : undefined,
            couple_friendly: filters.coupleFriendly ? 1 : undefined,
            pets_allowed: filters.petsAllowed ? 1 : undefined,
            sort: sortBy || undefined,
            page: pageNum,
            per_page: 8,
            include_ratehawk: 1,
        };

        // Clean undefined values
        Object.keys(queryParams).forEach(key => queryParams[key] === undefined && delete queryParams[key]);
        return queryParams;
    }, [city, filters, sortBy, location.search]);

    // Initial search or filter/query change
    useEffect(() => {
        if (!locationLoaded) return;

        const fetchHotels = async () => {
            setIsSearching(true);
            isSearchingRef.current = true;
            pageRef.current = 1;
            setPage(1);
            hasMoreRef.current = true;
            setHasMore(true);

            try {
                const queryParams = buildQueryParams(1);
                const response = await propertyService.searchProperties(queryParams);

                // Response comes wrapped in paginated format response.data.data from Laravel
                let hotelList = [];
                if (Array.isArray(response.ratehawk_hotels) && response.ratehawk_hotels.length > 0) {
                    hotelList = response.ratehawk_hotels;
                } else if (response.data && Array.isArray(response.data.data)) {
                    hotelList = response.data.data;
                } else if (Array.isArray(response.data)) {
                    hotelList = response.data;
                }

                // Strictly keep RateHawk hotels only and remove duplicates
                const seenIds = new Set();
                const approvedHotels = hotelList.filter(hotel => {
                    if (!hotel || !hotel.id || seenIds.has(hotel.id)) return false;
                    const isRateHawk = hotel.provider === 'ratehawk' || String(hotel.id).startsWith('rh_');
                    if (isRateHawk) {
                        seenIds.add(hotel.id);
                        return true;
                    }
                    return false;
                });

                const mappedHotels = approvedHotels.map(h => mapHotelToCard(h, navigate, location));
                setHotels(mappedHotels);

                const hasMoreFlag = response.has_more ?? response.data?.has_more ?? (approvedHotels.length >= 8);
                setHasMore(hasMoreFlag);
                hasMoreRef.current = hasMoreFlag;

            } catch (err) {
                console.error("Failed to load search results: ", err);
                setHotels([]);
            } finally {
                setIsSearching(false);
                isSearchingRef.current = false;
            }
        };

        fetchHotels();
    }, [city, filters, sortBy, location.search, navigate, locationLoaded, buildQueryParams]);

    // Infinite Scroll / Load More on Scroll
    const loadMoreHotels = async () => {
        if (isLoadingMoreRef.current || !hasMoreRef.current || isSearchingRef.current) return;

        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);

        try {
            const nextPage = pageRef.current + 1;
            const queryParams = buildQueryParams(nextPage);
            const response = await propertyService.searchProperties(queryParams);

            let hotelList = [];
            if (Array.isArray(response.ratehawk_hotels) && response.ratehawk_hotels.length > 0) {
                hotelList = response.ratehawk_hotels;
            } else if (response.data && Array.isArray(response.data.data)) {
                hotelList = response.data.data;
            } else if (Array.isArray(response.data)) {
                hotelList = response.data;
            }

            setHotels(prevHotels => {
                const existingIds = new Set(prevHotels.map(h => h.id));
                const newApproved = hotelList.filter(hotel => 
                    hotel && (hotel.provider === 'ratehawk' || String(hotel.id).startsWith('rh_')) && !existingIds.has(hotel.id)
                );

                if (newApproved.length === 0) {
                    hasMoreRef.current = false;
                    setHasMore(false);
                    return prevHotels;
                }

                const mappedNew = newApproved.map(h => mapHotelToCard(h, navigate, location));
                const moreFlag = response.has_more ?? response.data?.has_more ?? (newApproved.length >= 8);
                hasMoreRef.current = moreFlag;
                setHasMore(moreFlag);
                pageRef.current = nextPage;
                setPage(nextPage);

                return [...prevHotels, ...mappedNew];
            });
        } catch (err) {
            console.error("Failed to load more hotels on scroll:", err);
        } finally {
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
        }
    };

    // IntersectionObserver to detect when user scrolls to bottom of hotel list
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMoreRef.current && !isLoadingMoreRef.current && !isSearchingRef.current) {
                    loadMoreHotels();
                }
            },
            { rootMargin: '300px' }
        );

        const target = observerTarget.current;
        if (target) {
            observer.observe(target);
        }

        return () => {
            if (target) {
                observer.unobserve(target);
            }
        };
    }, [location.search, city, filters, sortBy, locationLoaded]);

    const quickFilters = [
        {
            label: 'Under ₹2000',
            active: filters.price === 2000,
            onClick: () => setFilters(prev => ({ ...prev, price: prev.price === 2000 ? 5000 : 2000 }))
        },
        {
            label: 'Top Rated (4.5+)',
            active: filters.rating === 4,
            onClick: () => setFilters(prev => ({ ...prev, rating: prev.rating === 4 ? null : 4 }))
        },
        {
            label: 'Free WiFi',
            active: filters.amenities.includes('Free WiFi'),
            onClick: () => setFilters(prev => {
                const has = prev.amenities.includes('Free WiFi');
                return {
                    ...prev,
                    amenities: has ? prev.amenities.filter(a => a !== 'Free WiFi') : [...prev.amenities, 'Free WiFi']
                };
            })
        },
        {
            label: 'Swimming Pool',
            active: filters.amenities.includes('Swimming Pool'),
            onClick: () => setFilters(prev => {
                const has = prev.amenities.includes('Swimming Pool');
                return {
                    ...prev,
                    amenities: has ? prev.amenities.filter(a => a !== 'Swimming Pool') : [...prev.amenities, 'Swimming Pool']
                };
            })
        },
        {
            label: 'Couple Friendly',
            active: filters.coupleFriendly,
            onClick: () => setFilters(prev => ({ ...prev, coupleFriendly: !prev.coupleFriendly }))
        }
    ];

    return (
        <div className="min-h-screen bg-[#F8FAFC] lg:pt-20 w-screen-10">
            <DefaultSEO title={`Hotels in ${city || 'India'}`} description={`Find the best hotels in ${city || 'India'} on ZoyoTrip. Compare prices, read reviews, and book easily.`} />
            <div className="flex gap-3">
                {/* Filter Sidebar - Desktop */}
                <FilterBar
                    showFilter={showFilter}
                    setShowFilter={setShowFilter}
                    filters={filters}
                    setFilters={setFilters}
                />

                {/* Main Content */}
                <main className="flex-1 w-100">
                    <SearchPageTopBar
                        hotelCount={hotels.length}
                        showSortSheet={showSortSheet}
                        setShowSortSheet={setShowSortSheet}
                        setShowFilter={setShowFilter}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        city={city}
                        isSearching={isSearching}
                    />

                    {/* Quick Filters - Mobile */}
                    <div className="lg:hidden mb-4 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        <div className="flex space-x-2 px-4 py-1 pb-2">
                            {quickFilters.map((item, idx) => (
                                <button
                                    key={idx}
                                    onClick={item.onClick}
                                    className={`flex-shrink-0 px-4 py-2.5 rounded-2xl text-[13px] font-semibold transition-all duration-300 ${
                                        item.active 
                                            ? 'bg-blue-600 text-white shadow-md shadow-blue-200/50 scale-105' 
                                            : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Hotel Cards List */}
                    <div className="lg:space-y-4 pb-28 lg:pb-0">
                        {isSearching ? (
                            <>
                                <HotelSkeletonCard />
                                <HotelSkeletonCard />
                                <HotelSkeletonCard />
                            </>
                        ) : hotels.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 px-4 text-center mt-4">
                                <div className="mb-6 animate-fade-in-up">
                                    <svg width="200" height="150" viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="40" y="20" width="120" height="100" rx="12" fill="#F3F4F6"/>
                                        <path d="M40 40h120v70c0 6.627-5.373 12-12 12H52c-6.627 0-12-5.373-12-12V40z" fill="#fff"/>
                                        <rect x="60" y="60" width="80" height="8" rx="4" fill="#E5E7EB"/>
                                        <rect x="60" y="80" width="50" height="8" rx="4" fill="#E5E7EB"/>
                                        <rect x="60" y="100" width="65" height="8" rx="4" fill="#E5E7EB"/>
                                        <circle cx="135" cy="105" r="30" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="6"/>
                                        <path d="M156 126l18 18" stroke="#3B82F6" strokeWidth="8" strokeLinecap="round"/>
                                        <path d="M125 95l20 20M145 95l-20 20" stroke="#3B82F6" strokeWidth="4" strokeLinecap="round"/>
                                        <circle cx="30" cy="40" r="6" fill="#D1D5DB"/>
                                        <circle cx="180" cy="70" r="4" fill="#D1D5DB"/>
                                        <circle cx="160" cy="20" r="8" fill="#9CA3AF" opacity="0.5"/>
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800 mb-2">No properties found</h3>
                                <p className="text-gray-500 max-w-md mx-auto text-sm">
                                    We couldn't find any properties matching your current filters in your search destination. Try adjusting your search criteria to see more results.
                                </p>
                            </div>
                        ) : (
                            <>
                                {hotels.map((hotel, index) => (
                                    <HotelCard key={hotel.id || index} {...hotel} />
                                ))}

                                {/* Infinite Scroll Trigger Sentinel */}
                                <div ref={observerTarget} className="h-6 w-full pointer-events-none" />

                                {/* Scroll Loading Spinner */}
                                {isLoadingMore && (
                                    <div className="flex flex-col items-center justify-center py-6 animate-fade-in">
                                        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                        <span className="text-xs text-gray-500 font-semibold mt-2.5">
                                            Loading more hotels...
                                        </span>
                                    </div>
                                )}

                                {/* End of list message */}
                                {!hasMore && hotels.length > 0 && !isSearching && (
                                    <div className="text-center py-8 text-gray-400 text-xs font-semibold">
                                        ✨ You've reached the end of available hotels in {city || 'this destination'}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default SearchPage;
